import { FC, useEffect, useState } from "react";

import { RangeLineChart } from "../../../Charts/RangeLineChart/RangeLineChart";
import { LineChartDataKey } from "../../../Charts/LineChart/LineChart.type";
import { useBreakpoint } from "../../../../hooks/useBreakpoint";
import { formatNumber } from "../../../../utils/formatNumber";

export interface AverageConsumptionDataPoint {
	time: number;
	minimum: number;
	average: number;
	maximum: number;
}

interface AverageConsumptionLineChartProps {
	data: Array<AverageConsumptionDataPoint>;
	yAxisLabel: string;
	chartSubtitle: string;
	yDomain: [number, number];
	yTickCount: number;
	dataKeys: Array<LineChartDataKey>;
}

const AverageConsumptionLineChart: FC<AverageConsumptionLineChartProps> = ({
	data,
	yAxisLabel,
	chartSubtitle,
	yDomain,
	yTickCount,
	dataKeys,
}) => {
	const chartSize = useBreakpoint();

	const [dotLabelFontSize, setDotLabelFontSize] = useState<number>(12);
	const [chartData, setChartData] = useState(data);
	const [axisFontSize, setAxisFontSize] = useState(12);
	const [marginLeft, setMarginLeft] = useState(0);
	const [xAxisPadding, setXAxisPadding] = useState(50);
	const [chartHeight, setChartHeight] = useState(450);

	useEffect(() => {
		switch (chartSize) {
			case "xs":
				setChartData(data.filter((dataEntry) => ["1998","2002", "2006", "2010", "2014", "2018", "2022"].includes(`${dataEntry.time}`)));
				setMarginLeft(-15);
				setAxisFontSize(9);
				setDotLabelFontSize(9);
				setXAxisPadding(20);
				setChartHeight(250);
				break;

			case "sm":
				setChartData(
					data.filter((dataEntry) => ["1998","2001", "2004", "2007", "2010", "2013", "2016", "2019", "2022"].includes(`${dataEntry.time}`)),
				);
				setMarginLeft(-5);
				setAxisFontSize(11);
				setDotLabelFontSize(11);
				setXAxisPadding(15);
				setChartHeight(350);
				break;

			case "md":
				setChartData(
					data.filter((dataEntry) => ["1998","2000", "2002", "2004", "2006", "2008", "2010", "2012", "2014", "2016", "2018", "2020", "2022"].includes(`${dataEntry.time}`)),
				);
				setMarginLeft(-5);
				setAxisFontSize(12);
				setDotLabelFontSize(12);
				setXAxisPadding(20);
				setChartHeight(350);
				break;

			case "lg":
				setChartData(
					data.filter((dataEntry) => ["1998","2000", "2002", "2004", "2006", "2008", "2010", "2012", "2014", "2016", "2018", "2020", "2022"].includes(`${dataEntry.time}`)),
				);
				setMarginLeft(0);
				setAxisFontSize(14);
				setDotLabelFontSize(11);
				setXAxisPadding(35);
				setChartHeight(450);
				break;

			case "xl":
				setMarginLeft(0);
				setChartData(
					data.filter((dataEntry) => ["1998","2000", "2002", "2004", "2006", "2008", "2010", "2012", "2014", "2016", "2018", "2020", "2022"].includes(`${dataEntry.time}`)),
				);
				setAxisFontSize(12);
				setDotLabelFontSize(11);
				setXAxisPadding(35);
				setChartHeight(450);
				break;

			case "2xl":
			default:
				setMarginLeft(0);
				setChartData(data);
				setAxisFontSize(14);
				setDotLabelFontSize(14);
				setXAxisPadding(35);
				setChartHeight(450);
				break;
		}
	}, [chartSize, data]);

	return (
		<div className="flex flex-col gap-8">
			<RangeLineChart
				YAxisLabel={yAxisLabel}
				yAxisLabelClassName="text-lg text-gray-800 font-bold"
				data={chartData}
				height={chartHeight}
				marginLeft={marginLeft}
				labelProps={{ fontSize: dotLabelFontSize, formatter: formatNumber }}
				XAxisProps={{
					dataKey: "time",
					padding: { left: xAxisPadding, right: xAxisPadding },
					tick: { fontSize: axisFontSize },
				}}
				xDomain={[1997, 2022]}
				customXTicks={[2000, 2005, 2010, 2015, 2020]}
				YAxisProps={{
					tickCount: yTickCount,
					tick: { fontSize: axisFontSize },
					tickFormatter: formatNumber,
					domain: yDomain,
					interval: 0,
				}}
				dataKeys={dataKeys.map((dataKey) => ({ ...dataKey, minName: "minimum", maxName: "maximum" }))}
				showToolTip={false}
				chartSubtitle={chartSubtitle}
			/>
		</div>
	);
};

export { AverageConsumptionLineChart };
